import { createAction } from 'redux-act';
import { paramsForServer } from 'feathers-hooks-common';
import { apolloClient } from 'meeopp-graphql/apollo-client';
//
import { KEYS } from 'utils/constants';
import {
  TopicService,
  QuestionTagService,
  SlideAssetService,
  AudioClipService,
  QuestionService,
  TopicTagService,
} from 'services';
import { setLoading } from '../loading/actions';
import { showNotification } from 'utils/appUtils';
import { showErrorNotification } from 'utils/error-handlers';
import { UPDATE_QUESTION, CREATE_QUESTION } from 'meeopp-graphql/modules/question';
import questionSchema from 'lib/question/validation';

export const fetchOrganizationTopicsSuccess = createAction('fetchOrganizationTopicsSuccess');
export const updateQuestionTopicSuccess = createAction('updateQuestionTopicSuccess');
export const addQuestionToTopicSuccess = createAction('addQuestionToTopicSuccess');
export const deleteTopicQuestionSuccess = createAction('deleteTopicQuestionSuccess');
export const changeTopicQuestionOrderSuccess = createAction('changeTopicQuestionOrderSuccess');
//
export const fetchQuestiontagsSuccess = createAction('fetchQuestiontagsSuccess');
export const updateQuestionTag = createAction('updateQuestionTag');
export const addQuestionTag = createAction('addQuestionTag');
//
export const fetchItemsSuccess = createAction('fetchItemsSuccess');
export const addItemIntoArraySuccess = createAction('addItemIntoArraySuccess');
export const updateItemInArraySuccess = createAction('updateItemInArraySuccess');
export const deleteItemInArraySuccess = createAction('deleteItemInArraySuccess');
export const clearCurriculumTopics = createAction('clearCurriculumTopics');

export const getTopicSuccess = createAction('getTopicSuccess');
export const getTopicByName = createAction('getTopicByName');

/**
 * @description Redux action for fetching Question tags from Server
 */
export const fetchQuestionsTags = () => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchQuestionTags,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const res = await QuestionTagService.find({
        query: {
          $sort: {
            createdAt: 1,
          },
        },
      });

      dispatch(fetchQuestiontagsSuccess(res.data));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

/**
 * @function updateQuestionTags
 * @description Redux action for updating QuestionTag
 * @param {Object} item
 */
export const updateQuestionTags = (item) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.updateQuestionTag,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const response = await QuestionTagService.patch(item._id, {
        title: item.title,
      });

      // update redux state
      dispatch(updateQuestionTag(response));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

/**
 * @description Insert Question Tag and update component
 * @param {string} title
 */
export const addQuestiontagAction = (title) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.addQuestiontag,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const response = await QuestionTagService.create({
        title: title,
      });

      dispatch(addQuestionTag(response));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

const _fetchTopicSlides = (topicId, { isPreview }) => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchTopicSlides,
        value: true,
      })
    );

    try {
      const data = await SlideAssetService.get(topicId);

      dispatch(
        fetchItemsSuccess({
          key: !isPreview ? 'slides' : 'previewSlides',
          value: data.Contents,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchTopicSlides, value: false }));
    }
  };
};

export const fetchTopicSlides = (topicId) => {
  return _fetchTopicSlides(topicId, { isPreview: false });
};

export const fetchTopicSlidesForPreview = (topicId) => {
  return _fetchTopicSlides(topicId, { isPreview: true });
};

const _fetchTopicAudioClips = (topicId, { isPreview }) => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchTopicAudioClips,
        value: true,
      })
    );

    try {
      const data = await AudioClipService.find(
        paramsForServer({
          query: {
            curriculumId: topicId,
          },
        })
      );

      dispatch(
        fetchItemsSuccess({
          key: !isPreview ? 'audioClips' : 'previewAudioClips',
          value: data,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchTopicAudioClips, value: false }));
    }
  };
};

export const fetchTopicAudioClips = (topicId) => {
  return _fetchTopicAudioClips(topicId, { isPreview: false });
};

export const fetchTopicAudioClipsForPreview = (topicId) => {
  return _fetchTopicAudioClips(topicId, { isPreview: true });
};

const _fetchTopicQuestions = (topicId, { isPreview }) => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchTopicQuestions,
        value: true,
      })
    );

    try {
      const data = await QuestionService.find({
        query: {
          topicId,
          $sort: {
            order: 1,
          },
        },
      });

      dispatch(
        fetchItemsSuccess({
          key: !isPreview ? 'questions' : 'previewQuestions',
          value: data,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchTopicQuestions, value: false }));
    }
  };
};

export const fetchTopicQuestions = (topicId) => {
  return _fetchTopicQuestions(topicId, { isPreview: false });
};

export const fetchTopicQuestionsForPreview = (topicId) => {
  return _fetchTopicQuestions(topicId, { isPreview: true });
};

export const addQuestionToTopic = (payload, gradingSettingsInput) => {
  return async (dispatch) => {
    if (payload.parentId) {
      payload.topicId = payload.parentId;
    }

    delete payload.parentId;
    let qsn;
    if (payload._id) {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_QUESTION,
        variables: {
          questionId: payload._id,
          input: questionSchema.validateSync(payload, { stripUnknown: true }),
          gradingSettingsInput,
        },
      });

      const qsn = data.updateQuestion;
      dispatch(updateQuestionTopicSuccess(qsn));
    } else {
      delete payload._id;
      const { data } = await apolloClient.mutate({
        mutation: CREATE_QUESTION,
        variables: {
          input: questionSchema.validateSync(payload, { stripUnknown: true }),
          gradingSettingsInput,
        },
      });

      qsn = data.createQuestion;
      dispatch(addQuestionToTopicSuccess(qsn));
    }

    return qsn;
  };
};

export const deleteTopicQuestion = (id) => {
  return async (dispatch) => {
    const qsn = await QuestionService.remove(id);

    dispatch(deleteTopicQuestionSuccess(qsn));
    return qsn;
  };
};

export const changeTopicQuestionsOrder = (payload) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchTopicQuestions,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      await Promise.all(
        payload.questions.map(async (q) => {
          const { data } = await apolloClient.mutate({
            mutation: UPDATE_QUESTION,
            variables: {
              questionId: q._id,
              input: questionSchema.validateSync({ order: q.order }, { stripUnknown: true }),
            },
          });

          return data.updateQuestion;
        })
      );

      dispatch(changeTopicQuestionOrderSuccess(payload.index));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    }

    loadingObj.value = false;
    dispatch(setLoading(loadingObj));
  };
};

export const createTopicTag = (title) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.createTopicTag,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      await TopicTagService.create({
        title,
      });
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

export const getTopic = (id) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.getTopic,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const data = await TopicService.get(id);
      dispatch(getTopicSuccess(data));
    } catch (e) {
      showNotification('error', 'Something went wrong!');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

export const findTopicByTitle = (title) => {
  return async (dispatch) => {
    const loadingObj = {
      key: KEYS.fetchTopicByTitle,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const data = await TopicService.find(
        paramsForServer({
          query: {
            title,
          },
          action: 'fetch-topics-by-title',
        })
      );

      dispatch(getTopicByName(data));
    } catch (err) {
      showErrorNotification();
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};
